import React, { useCallback } from 'react';
import { SelectOption, SelectTWProps, SelectValue } from './interfaces';
import { useField } from 'formik';
import { useErrorText } from '../hooks/useErrorText';
import { ComboBoxMultipleTW, ComboBoxTW, CreatableComboBoxTW } from './combobox/ComboBoxTW';

interface SingleSelectFieldTWProps extends SelectTWProps {
  autoComplete?: string;
  isMultiple?: false;
  isNullable?: boolean;
  isCreatable?: boolean;
  name: string;
  onChange?: (option: SelectOption) => void;
  onClearSelectionProp?: () => void;
  isMenuPlacementTop?: boolean;
  formatOptionLabel?: (option: SelectOption) => React.ReactNode;
}

interface MultiSelectFieldTWProps extends SelectTWProps {
  isMultiple: true;
  isCreatable?: false;
  name: string;
  onChange?: (option: SelectValue[]) => void;
}

type SelectFieldTWProps = SingleSelectFieldTWProps | MultiSelectFieldTWProps;

/*
 * In practice, this intended to be the sole "entry point" for a drop-down or <select>-like tag
 * The different implementations and their details are "hidden" from the developer.
 * The implementations are built with Tailwind and HeadlessUI
 * The developer chooses which implementation they want with props: isCreatable and isMultiple
 * isCreatable is implemented only for single select.
 * */
export const SelectFieldTW: React.FC<SelectFieldTWProps> = ({ isMultiple, isCreatable, onChange, ...rest }) => {
  if (isMultiple) {
    return <SelectFieldTWMultiple {...rest} onChange={onChange} />;
  }
  if (isCreatable) {
    return <SelectFieldTWSingleCreatable {...rest} onChange={onChange} />;
  }
  return <SelectFieldTWSingle {...rest} onChange={onChange} />;
};

const SelectFieldTWSingle: React.FC<SingleSelectFieldTWProps> = ({ name, isCreatable, options, ...rest }) => {
  const { errorText, fieldInputProps, onChange, onClearSelection, onBlur } = useGetSingleSelectDataAndMethods(
    name,
    rest.onChange,
    rest.onClearSelectionProp
  );

  const boxValue: SelectOption | null =
    options.find((option: SelectOption) => option.value === fieldInputProps.value) || null;

  return (
    <ComboBoxTW
      options={options}
      errorText={errorText}
      {...rest}
      {...fieldInputProps}
      onChange={onChange}
      onClearSelection={onClearSelection}
      onBlur={onBlur}
      value={boxValue}
    />
  );
};

const SelectFieldTWMultiple: React.FC<Omit<MultiSelectFieldTWProps, 'isMultiple'>> = ({ name, options, ...rest }) => {
  const [fieldInputProps, fieldMetaProps, fieldHelperProps] = useField<SelectValue[]>({ name });
  const errorText = useErrorText<SelectValue[]>(fieldMetaProps);

  const onChange = useCallback(
    (option: SelectOption[]) => {
      const newValues = option.map((x) => x.value);
      fieldHelperProps.setTouched(true);
      fieldHelperProps.setValue(newValues);
      if (rest.onChange) {
        rest.onChange(newValues);
      }
    },
    [fieldHelperProps, rest]
  );

  const boxValue: SelectOption[] | undefined = !!fieldInputProps?.value?.length
    ? options.filter((option: SelectOption) => fieldInputProps.value.includes(option.value))
    : undefined;

  return (
    <ComboBoxMultipleTW
      options={options}
      errorText={errorText}
      {...rest}
      {...fieldInputProps}
      onChange={onChange}
      value={boxValue}
    />
  );
};

const SelectFieldTWSingleCreatable: React.FC<SingleSelectFieldTWProps> = ({ name, options, ...rest }) => {
  const { errorText, fieldInputProps, onChange, onBlur } = useGetSingleSelectDataAndMethods(name, rest.onChange);

  const boxValue = options.find((option: SelectOption) => option.value === fieldInputProps.value) ?? {
    label: String(fieldInputProps.value),
    value: fieldInputProps.value,
  };

  return (
    <CreatableComboBoxTW
      options={options}
      errorText={errorText}
      {...rest}
      {...fieldInputProps}
      onChange={onChange}
      onBlur={onBlur}
      value={boxValue}
    />
  );
};

const useGetSingleSelectDataAndMethods = (
  name: string,
  onChangeProp?: (option: SelectOption) => void,
  onClearSelectionProp?: () => void,
  onBlurProp?: () => void
) => {
  const [fieldInputProps, fieldMetaProps, fieldHelperProps] = useField<SelectValue>({ name });
  const errorText = useErrorText<SelectValue>(fieldMetaProps);

  const onChange = useCallback(
    (option: SelectOption) => {
      fieldHelperProps.setTouched(true);
      fieldHelperProps.setValue(option.value);
      if (onChangeProp) {
        onChangeProp(option);
      }
    },
    [fieldHelperProps, onChangeProp]
  );

  const onBlur = useCallback(() => {
    fieldHelperProps.setTouched(true);
    if (onBlurProp) {
      onBlurProp();
    }
  }, [fieldHelperProps, onBlurProp]);

  const onClearSelection = useCallback(() => {
    fieldHelperProps.setTouched(true);
    fieldHelperProps.setValue(null);
    if (onClearSelectionProp) {
      onClearSelectionProp();
    }
  }, [fieldHelperProps, onClearSelectionProp]);

  return { errorText, fieldInputProps, onChange, onClearSelection, onBlur };
};
